/**
 * Text Component
 *
 * Unified typography primitive. Variants map to semantic tags + theme tokens.
 * Weight/size/color/as override variant defaults when provided.
 *
 * A11y:
 * - Heading variants render semantic h1–h6 tags by default.
 * - Error variant auto-applies `role="alert"` for screen readers.
 * - Link variant includes focus-visible styles and underline on hover.
 * - `target="_blank"` auto-adds `rel="noopener noreferrer"`.
 * - `truncate` consumers should pass `title` for accessible full text.
 */

'use client';

import styled from '@emotion/styled';
import {
  ComponentProps,
  CSSProperties,
  ElementType,
  HTMLAttributes,
  ReactNode,
} from 'react';

export type TextVariant =
  | 'heading1'
  | 'heading2'
  | 'heading3'
  | 'heading4'
  | 'heading5'
  | 'heading6'
  | 'body'
  | 'bodySmall'
  | 'caption'
  | 'captionSmall'
  | 'label'
  | 'labelCaps'
  | 'error'
  | 'link'
  | 'button';

export type TextWeight = 'regular' | 'semibold' | 'bold' | 'heavy';

/** Preferred size tokens. Legacy aliases (`small`/`regular`/`large`) still accepted. */
export type TextSize = 'sm' | 'md' | 'lg' | 'small' | 'regular' | 'large';

export type TextAlign = 'left' | 'center' | 'right';
export type TextTransform = 'uppercase' | 'capitalize' | 'lowercase' | 'none';

const DEFAULT_TAGS: Record<TextVariant, ElementType> = {
  heading1: 'h1',
  heading2: 'h2',
  heading3: 'h3',
  heading4: 'h4',
  heading5: 'h5',
  heading6: 'h6',
  body: 'p',
  bodySmall: 'p',
  caption: 'span',
  captionSmall: 'span',
  label: 'label',
  labelCaps: 'span',
  error: 'span',
  link: 'a',
  button: 'span',
};

const SIZE_SCALE: Record<string, number> = {
  sm: 0.875,
  small: 0.875,
  md: 1,
  regular: 1,
  lg: 1.125,
  large: 1.125,
};

export interface TextProps extends Omit<HTMLAttributes<HTMLElement>, 'color' | 'children'> {
  children?: ReactNode;
  variant?: TextVariant;
  /** Override variant default weight. Omit to keep variant weight. */
  weight?: TextWeight;
  /** Relative size shift within variant. Defaults to md (1x). */
  size?: TextSize;
  /** Theme color path key or raw CSS color. Defaults to theme text primary / error color. */
  color?: string;
  /** Override semantic tag for the variant. */
  as?: ElementType;
  truncate?: boolean;
  align?: TextAlign;
  transform?: TextTransform;
  className?: string;
  style?: CSSProperties;
  /** Link-specific (when variant=`link` or as=`a`) */
  href?: string;
  target?: string;
  rel?: string;
}

type StyledTextProps = {
  $variant: TextVariant;
  $weight?: TextWeight;
  $size: TextSize;
  $color?: string;
  $truncate?: boolean;
  $align?: TextAlign;
  $transform?: TextTransform;
};

/** Optional keys exist only on some theme variants (error/labelCaps). */
type TextVariantStyle = {
  fontSize?: string;
  lineHeight?: string;
  fontWeight?: number;
  tag?: string;
  color?: string;
  textTransform?: string;
  letterSpacing?: string;
};

const StyledText = styled.span<StyledTextProps>`
  margin: 0;
  padding: 0;
  font-size: ${({ theme, $variant, $size }) => {
    const style = theme?.component?.text?.variants?.[$variant] as TextVariantStyle | undefined;
    const base = style?.fontSize ?? '1rem';
    const scale = SIZE_SCALE[$size] ?? 1;
    return scale === 1 ? base : `calc(${base} * ${scale})`;
  }};
  line-height: ${({ theme, $variant }) => {
    const style = theme?.component?.text?.variants?.[$variant] as TextVariantStyle | undefined;
    return style?.lineHeight ?? '1.5';
  }};
  font-weight: ${({ theme, $variant, $weight }) => {
    if ($weight) {
      return theme?.component?.text?.weights?.[$weight] ?? 400;
    }
    const style = theme?.component?.text?.variants?.[$variant] as TextVariantStyle | undefined;
    return style?.fontWeight ?? 400;
  }};
  color: ${({ theme, $variant, $color }) => {
    if ($color) return $color;
    if ($variant === 'error') {
      const errorStyle = theme?.component?.text?.variants?.error as
        | TextVariantStyle
        | undefined;
      return (
        errorStyle?.color ??
        theme?.colors?.semantic?.urgent?.color ??
        '#c64844'
      );
    }
    if ($variant === 'link') {
      // link variant has no theme color token; inherit text primary
      return (
        theme?.colors?.text?.primary ??
        theme?.colors?.text?.color ??
        'inherit'
      );
    }
    return theme?.colors?.text?.primary ?? theme?.colors?.text?.color ?? 'inherit';
  }};
  text-transform: ${({ theme, $variant, $transform }) => {
    if ($transform) return $transform;
    const style = theme?.component?.text?.variants?.[$variant] as TextVariantStyle | undefined;
    return style?.textTransform ?? 'none';
  }};
  letter-spacing: ${({ theme, $variant }) => {
    const style = theme?.component?.text?.variants?.[$variant] as TextVariantStyle | undefined;
    return style?.letterSpacing ?? 'normal';
  }};
  text-align: ${({ $align }) => $align ?? 'inherit'};
  cursor: ${({ $variant }) => ($variant === 'link' ? 'pointer' : 'inherit')};
  text-decoration: ${({ $variant }) => ($variant === 'link' ? 'none' : 'inherit')};

  /* Link a11y: underline on hover/focus so links are distinguishable (WCAG 1.4.1) */
  ${({ $variant }) =>
    $variant === 'link'
      ? `
    &:hover,
    &:focus {
      text-decoration: underline;
    }
    &:focus-visible {
      outline: 2px solid currentColor;
      outline-offset: 2px;
      text-decoration: underline;
    }
  `
      : ''}

  ${({ $truncate }) =>
    $truncate
      ? `
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `
      : ''}
`;

export function Text({
  children,
  variant = 'body',
  weight,
  size = 'md',
  color,
  as,
  truncate,
  align,
  transform,
  className,
  style,
  href,
  target,
  rel,
  ...rest
}: TextProps) {
  const tag = as ?? DEFAULT_TAGS[variant] ?? 'span';
  const isAnchor = variant === 'link' || tag === 'a';

  // Auto-add rel="noopener noreferrer" for target="_blank" links (security + a11y)
  const anchorProps: Record<string, unknown> = {};
  if (isAnchor) {
    anchorProps.href = href;
    anchorProps.target = target;
    if (target === '_blank') {
      anchorProps.rel = rel ?? 'noopener noreferrer';
    } else if (rel) {
      anchorProps.rel = rel;
    }
  }

  // Error variant auto-applies role="alert" for screen reader announcement
  const a11yProps: Record<string, unknown> = {};
  if (variant === 'error' && !rest.role) {
    a11yProps.role = 'alert';
  }

  const props = {
    as: tag,
    $variant: variant,
    $weight: weight,
    $size: size,
    $color: color,
    $truncate: truncate,
    $align: align,
    $transform: transform,
    className,
    style,
    ...anchorProps,
    ...a11yProps,
    ...rest,
    children,
  };

  return <StyledText {...(props as ComponentProps<typeof StyledText>)} />;
}
